import { Link } from 'react-router-dom';

const sections = [
  {
    id: 'overview',
    title: '1. Overview',
    content: `SisaTeck ("the Platform", "we", "our", or "us") is an APK hosting and distribution platform for Android applications. This Privacy Policy explains how we handle information when you use our platform, website, and services.

We are committed to protecting your privacy. SisaTeck is designed with a privacy-first approach — we only collect essential information needed to provide our hosting services and improve your experience.`,
  },
  {
    id: 'data-collected',
    title: '2. Information We Collect',
    content: `We collect minimal information necessary to operate and improve the Platform:

**Information You Provide:**
- Contact information (name, phone number, email) when you reach out to us for support, hosting inquiries, or admin access.
- Messages and communications you send to us via WhatsApp, phone, or our contact form.
- App information when you upload applications (app name, package name, version, description, screenshots, APK files).

**Automatically Collected Information:**
- Download statistics (number of downloads, download dates) for analytics purposes.
- Basic usage data (page views, app ratings, reviews) to improve our platform.
- IP addresses and browser information for security and fraud prevention.

**What We Do NOT Collect:**
- Personal identifiable information beyond what you voluntarily provide.
- Your device's location data.
- Data from apps you download (we only host APK files).
- Payment card information (we handle transactions via direct communication).`,
  },
  {
    id: 'hosted-apps',
    title: '3. Hosted Applications',
    content: `SisaTeck serves as a hosting platform for Android APK files. Here's how we handle app data:

- We **host and distribute** APK files uploaded by app developers or administrators.
- We **do not** modify, reverse engineer, or inject code into hosted applications.
- We **do not** monitor or collect data from apps after they are downloaded.
- We **are not responsible** for the privacy practices of individual apps hosted on our platform.
- Each app has its own privacy policy — we encourage users to review them before downloading.

Your privacy when using downloaded apps is governed by those apps' individual privacy policies, not ours.`,
  },
  {
    id: 'how-we-use',
    title: '4. How We Use Your Information',
    content: `The limited information we collect is used solely for:

- **Platform Operations:** Managing app hosting, downloads, and distribution services.
- **Customer Support:** Responding to your inquiries, troubleshooting issues, and providing technical assistance.
- **Admin Access:** Managing administrator accounts for app uploading and management.
- **Analytics:** Understanding which apps are popular to improve our platform and recommendations.
- **Communication:** Sending important updates about platform features or your hosted apps if you have opted in.

We do not use your information for advertising, profiling, or any commercial purpose beyond supporting our hosting platform.`,
  },
  {
    id: 'sharing',
    title: '5. Information Sharing & Disclosure',
    content: `We do not sell, trade, rent, or share your personal information with third parties, except in the following limited circumstances:

- **Legal Requirements:** If required by law, court order, or government authority.
- **Safety:** To protect the rights, property, or safety of our users or the public.
- **Business Transfer:** In the event of a merger or acquisition, your information may be transferred with appropriate notice.
- **App Developers:** If you upload apps, your contact information may be shared with users who contact you about your app.

We do not share your data with advertisers, data brokers, or analytics companies.`,
  },
  {
    id: 'third-party',
    title: '6. Third-Party Services',
    content: `Our platform uses minimal third-party services:

- **WhatsApp:** For customer support and communication (subject to WhatsApp's privacy policy).
- **Cloud Hosting:** APK files and images are stored on secure cloud infrastructure.

Our website may contain links to third-party websites or apps. We are not responsible for the privacy practices of those websites or applications. We encourage you to review their privacy policies.

We do not integrate third-party analytics SDKs, advertising networks, or tracking libraries beyond what's necessary for platform operations.`,
  },
  {
    id: 'security',
    title: '7. Data Security',
    content: `We take reasonable measures to protect any information you share with us:

- All data transmissions are encrypted using industry-standard SSL/TLS protocols.
- Contact messages and user data are stored securely and accessed only by authorized administrators.
- APK files are scanned for malware before being hosted (when possible).
- We do not store payment card information — transactions are handled through direct communication.
- We regularly review our security practices to ensure your information is protected.

However, no method of transmission over the internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.`,
  },
  {
    id: 'childrens',
    title: "8. Children's Privacy",
    content: `SisaTeck is not directed at children under the age of 13. We do not knowingly collect personal information from children under 13.

Some apps hosted on our platform may be suitable for children, but we are not responsible for age-appropriate content. Parents should review individual apps before allowing children to download them.

If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately and we will take steps to delete such information.`,
  },
  {
    id: 'your-rights',
    title: '9. Your Rights',
    content: `You have the following rights regarding your personal information:

- **Access:** Request a copy of the personal information we hold about you.
- **Correction:** Request correction of inaccurate or incomplete information.
- **Deletion:** Request deletion of your personal information from our records.
- **Opt-Out:** Opt out of any communications from us at any time.

To exercise any of these rights, contact us via WhatsApp at +254 793 973 389 or +254 717 325 657.`,
  },
  {
    id: 'changes',
    title: '10. Changes to This Policy',
    content: `We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons.

When we make significant changes, we will update the "Last Updated" date at the top of this page. We encourage you to review this policy periodically.

Continued use of the App after changes are posted constitutes your acceptance of the updated policy.`,
  },
  {
    id: 'contact',
    title: '11. Contact Us',
    content: `If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:

**SisaTeck Support Team**
📞 Phone / WhatsApp: +254 793 973 389
📞 Alternative: +254 717 325 657
💬 WhatsApp is our preferred and fastest contact method.

We aim to respond to all privacy-related inquiries within 48 hours.`,
  },
];

const PrivacyPolicy = () => {
  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen pt-16">
      {/* Header */}
      <div className="relative bg-[#111111] border-b border-[#2a2a2a] py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-green-500/5 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <span className="text-green-500 text-xs font-bold uppercase tracking-widest">Legal</span>
              <h1 className="text-4xl sm:text-5xl font-black mt-2 mb-3">
                Privacy <span className="text-gradient">Policy</span>
              </h1>
              <p className="text-gray-400 text-sm">
                Last Updated: <span className="text-gray-300">April 2026</span>
              </p>
            </div>
            <div className="flex-shrink-0">
              <div className="bg-green-500/10 border border-green-500/20 rounded-2xl px-5 py-4 text-center">
                <div className="text-3xl mb-1">🔒</div>
                <div className="text-green-400 text-xs font-bold uppercase tracking-wider">Privacy First</div>
                <div className="text-gray-500 text-xs mt-0.5">Minimal data collection</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
          {/* Sidebar TOC */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 bg-[#111111] border border-[#2a2a2a] rounded-2xl p-5">
              <h3 className="text-white font-bold text-sm mb-4 uppercase tracking-wider">Contents</h3>
              <nav className="space-y-1">
                {sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="block text-gray-500 hover:text-green-400 text-xs py-1.5 px-2 rounded-lg hover:bg-green-500/5 transition-all duration-150 leading-snug"
                  >
                    {s.title}
                  </a>
                ))}
              </nav>

              <div className="mt-6 pt-5 border-t border-[#2a2a2a]">
                <p className="text-gray-600 text-xs mb-3">Questions about this policy?</p>
                <a
                  href="https://wa.me/254793973389"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-1.5 bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 text-green-400 text-xs font-semibold px-3 py-2.5 rounded-lg transition-all w-full"
                >
                  💬 Contact Us
                </a>
              </div>
            </div>
          </aside>

          {/* Content */}
          <main className="lg:col-span-3">
            {/* Intro Banner */}
            <div className="bg-green-500/5 border border-green-500/15 rounded-2xl p-5 mb-8 flex gap-4 items-start">
              <div className="text-2xl flex-shrink-0">✅</div>
              <div>
                <h3 className="text-green-400 font-bold text-sm mb-1">Our Privacy Commitment</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  SisaTeck is a privacy-focused APK hosting platform. We only collect essential information needed to operate our services. We never sell your data or use it for advertising. Your privacy is our priority.
                </p>
              </div>
            </div>

            {/* Sections */}
            <div className="space-y-10">
              {sections.map((section) => (
                <div key={section.id} id={section.id} className="scroll-mt-24">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                    <span className="w-1 h-6 bg-green-500 rounded-full flex-shrink-0" />
                    {section.title}
                  </h2>
                  <div className="bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6">
                    <div className="text-gray-400 text-sm leading-relaxed space-y-3">
                      {section.content.split('\n\n').map((para, i) => {
                        if (para.startsWith('**') && para.includes(':**')) {
                          const parts = para.split('\n');
                          return (
                            <div key={i} className="space-y-1.5">
                              {parts.map((line, j) => {
                                if (line.startsWith('**') && line.endsWith('**')) {
                                  return <p key={j} className="text-white font-semibold text-sm">{line.replace(/\*\*/g, '')}</p>;
                                }
                                if (line.startsWith('- **')) {
                                  const match = line.match(/- \*\*(.+?)\*\*:? ?(.*)/);
                                  if (match) {
                                    return (
                                      <p key={j} className="text-sm pl-3 border-l-2 border-green-500/20">
                                        <span className="text-green-400 font-semibold">{match[1]}:</span>{' '}
                                        <span className="text-gray-400">{match[2]}</span>
                                      </p>
                                    );
                                  }
                                }
                                if (line.startsWith('- ')) {
                                  return <p key={j} className="text-sm pl-3 border-l-2 border-[#2a2a2a] text-gray-400">{line.slice(2)}</p>;
                                }
                                return line ? <p key={j}>{line}</p> : null;
                              })}
                            </div>
                          );
                        }
                        const formatted = para
                          .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>')
                          .replace(/📞/g, '📞')
                          .replace(/💬/g, '💬');
                        return (
                          <p key={i} dangerouslySetInnerHTML={{ __html: formatted }} />
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom CTA */}
            <div className="mt-12 bg-[#111111] border border-[#2a2a2a] rounded-2xl p-6 text-center">
              <h3 className="text-white font-bold text-lg mb-2">Still Have Questions?</h3>
              <p className="text-gray-500 text-sm mb-5">
                Our team is happy to answer any questions about how we handle your data.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="https://wa.me/254793973389"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-400 text-black font-bold px-6 py-3 rounded-xl text-sm transition-all hover:-translate-y-0.5"
                >
                  💬 WhatsApp Us
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/5 text-white font-semibold px-6 py-3 rounded-xl border border-[#2a2a2a] hover:border-green-500/40 text-sm transition-all"
                >
                  📬 Contact Form
                </Link>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
